import React, { useRef, useState } from 'react';
import { bool, string, func, array, object } from 'prop-types';
import classnames from 'classnames';
import ReportCardContainer from './ReportCardContainer';
import { Modal } from 'ps-design';
import { Button, ButtonGroup } from '../../../../ps-aspect-design';

import Grid from '../../../Grid/Grid';

ReportCards.propTypes = {
  userReportViewer: bool,
  userCanSelect: bool,
  reports: array,
  collections: array,
  userId: string,
  userEmail: string,
  groupId: string,
  reportWriterUrl: string,
  reportBulkSelect: func,
  launchDarklyFeatureFlags: object,
  popularFilter: bool,
  onSelectPopular: func,
  selectedPopularReports: array,
  deleteCollection: func,
  refresh: func,
  userSiteAdmin: bool,
  userGroupAdmin: bool
};

export default function ReportCards(props) {
  const [selectedCollectionId, setSelectedCollectionId] = useState();
  let deleteCollectionModalRef = useRef();

  function getIsPopularSelected(collectionId, reportId) {
    const found = props.selectedPopularReports.find(r => {
      return r.collectionId === collectionId && r.reportId === reportId;
    });

    if (found) return true;
    return false;
  }

  function getCollectionCards(reportIds, collectionId, canModifyCollection) {
    const cards = [];
    reportIds?.forEach(id => {
      const report = props.reports?.find(r => Number(r.id) === id);
      if (report) {
        cards.push(
          <ReportCardContainer
            userReportViewer={props.userReportViewer}
            userCanSelect={canModifyCollection}
            key={report.id}
            report={report}
            reportUrl={`${props.reportWriterUrl}/${report.id}`}
            launchDarklyFeatureFlags={props.launchDarklyFeatureFlags}
            userId={props.userId}
            userEmail={props.userEmail}
            groupId={props.groupId}
            onSelectPopular={checked => props.onSelectPopular(collectionId, Number(report.id), checked)}
            popularFilter={props.popularFilter}
            isPopularSelected={getIsPopularSelected(collectionId, Number(report.id))}
          />
        );
      }
    });

    return cards;
  }

  function deleteCollection() {
    if (props.deleteCollection && selectedCollectionId) {
      deleteCollectionModalRef?.hide();
      props.deleteCollection({ collectionId: selectedCollectionId }).then(status => {
        if (status === 200) {
          props.refresh();
        }
      });
    }
  }

  function getCollections() {
    const collectionCards = props.collections?.map(collection => {
      let collectionType;
      let collectionTypeName;
      if (collection.org_group_id) {
        collectionType = 'group';
        collectionTypeName = 'Group Collection';
      } else if (collection.organization_id) {
        collectionType = 'site';
        collectionTypeName = 'Site Collection';
      } else {
        collectionType = 'global';
        collectionTypeName = 'Standard Collection';
      }

      const canModifyCollection =
        collectionType !== 'global' &&
        ((collectionType === 'group' && props.userGroupAdmin) || (collectionType === 'site' && props.userSiteAdmin));

      const collectionCards = getCollectionCards(collection.reports, collection.id, canModifyCollection);
      const hasDescription = collection.metadata?.description;
      const isCollectionHidden = props.hiddenCollections.includes(collection.id);

      if (collectionCards.length > 0) {
        return (
          <div className="report-collection" key={collection.name}>
            <div className="report-collection__title">
              <i
                className={classnames('report-collection__toggle', {
                  'icon-up-open': !isCollectionHidden,
                  'icon-down-open': isCollectionHidden
                })}
                onClick={(id, checked) => props.toggleCollections(collection.id, null)}
              />
              <span
                onClick={(id, checked) => props.toggleCollections(collection.id, null)}
              >{`${collection.name} (${collectionCards.length})`}</span>
              <span className="report-collection__type">{collectionTypeName}</span>

              {canModifyCollection && (
                <div className="report-collection__delete">
                  <i
                    onClick={() => {
                      setSelectedCollectionId(collection.id);
                      deleteCollectionModalRef?.show();
                    }}
                    className="icon-trash"
                  />
                </div>
              )}
            </div>
            {hasDescription && !isCollectionHidden && (
              <div className="report-collection__description">{collection.metadata.description}</div>
            )}
            <div
              className={classnames('report-collection__cards', {
                'report-collection__cards-hidden': isCollectionHidden
              })}
            >
              <Grid className="report__cards">{collectionCards}</Grid>
            </div>
          </div>
        );
      }

      return null;
    });

    return (
      <>
        {collectionCards}

        <Modal
          ref={deleteCollectionModal => {
            deleteCollectionModalRef = deleteCollectionModal;
          }}
          id="deleteCollectionModal"
          className="modal__delete-collection"
          displayCloseButton={true}
        >
          <div className="modal__delete-contents">
            <div className="modal__delete-title">Are you sure you want to delete this collection?</div>
            <div className="modal__delete-actions">
              <ButtonGroup>
                <Button variant="secondary" onClick={() => deleteCollectionModalRef.hide()}>
                  No
                </Button>
                <Button variant="primary" onClick={deleteCollection} data-testid="delete-collection-confirm__btn">
                  Yes
                </Button>
              </ButtonGroup>
            </div>
          </div>
        </Modal>
      </>
    );
  }

  if (props.popularFilter) {
    return getCollections();
  }

  return (
    <Grid className="report__cards">
      {props.reports.map(report => {
        const canModifyReport = props.userSiteAdmin || props.userGroupAdmin || report.scope.code === 'personal';

        return (
          <ReportCardContainer
            userReportViewer={props.userReportViewer}
            userCanSelect={canModifyReport}
            key={report.id}
            report={report}
            reportUrl={`${props.reportWriterUrl}/${report.id}`}
            launchDarklyFeatureFlags={props.launchDarklyFeatureFlags}
            userId={props.userId}
            userEmail={props.userEmail}
            groupId={props.groupId}
            reportBulkSelect={props.reportBulkSelect}
          />
        );
      })}
    </Grid>
  );
}
